import { get } from "lodash"
import { createSelector } from "reselect"
import { ReduxState } from "model/state"
import { getRole } from "helpers/permissions/roles"
import { Role } from "model/state"

const getAccountStatus = (state: ReduxState): string | undefined =>
  get(state, "global.selected.accountMeta.status")

const getIsSuperuser = (state: ReduxState): boolean =>
  get(state, "user.isSuperuser", false)

const getCurrentAccount = (state: ReduxState): string | undefined =>
  get(state, "global.selected.account")

const getSolutionsCountId = (state: ReduxState): string | undefined =>
  get(state, "global.data.solutionsCount.id")

const getGenericSolutionsCount = (state: ReduxState): number | undefined =>
  get(state, "global.data.solutionsCount.data.generic")

const getSigmaSolutionsCount = (state: ReduxState): number | undefined =>
  get(state, "global.data.solutionsCount.data.sigma")

const getLicensedSkillsCount = (state: ReduxState): number | undefined =>
  get(state, "global.data.solutionsCount.data.licensedSkills")

const getProprietarySkillsCount = (state: ReduxState): number | undefined =>
  get(state, "global.data.solutionsCount.data.proprietarySkills")

// The AI nav item and its /home/ai route are both gated on skills_delivery. This local selector
// reads the flag directly so getHasAgenticWorkflows can stay a pure reselect selector.
const getIsSkillsDeliveryEnabled = (state: ReduxState): boolean =>
  get(state, "global.data.enabledFeatures.data.skills_delivery", false)

// True only when the stored solutionsCount resource was fetched for the currently-selected account.
// Passthrough resources persist across account switches, so this guards against showing the previous
// account's nav state while the new request is still in flight.
const getIsSolutionsCountCurrent = createSelector(
  [getCurrentAccount, getSolutionsCountId],
  (account: string | undefined, solutionsCountId: string | undefined): boolean => {
    if (!account || !solutionsCountId) return false
    return solutionsCountId.startsWith(`/api/${account}/`)
  },
)

const getGenericSolutionsAvailable = createSelector(
  [getGenericSolutionsCount, getRole, getIsSuperuser],
  (genericSolutionsCount: number | undefined, role: Role, isSuperuser: boolean): boolean => {
    if (!genericSolutionsCount) {
      return false
    }
    if (!isSuperuser && role !== "admin") {
      return false
    }
    return true
  },
)

export const getIsSuperuserSession = createSelector(
  [getIsSuperuser, getAccountStatus],
  (isSuperuser: boolean, accountStatus: string | undefined): boolean =>
    isSuperuser && accountStatus !== "internal",
)

export const getHasSolutions = createSelector(
  [getGenericSolutionsAvailable, getSigmaSolutionsCount, getIsSolutionsCountCurrent],
  (
    genericSolutionsAvailable: boolean,
    sigmaSolutionsCount: number | undefined,
    isCurrent: boolean,
  ): boolean => {
    if (!isCurrent) return false
    return genericSolutionsAvailable || !!sigmaSolutionsCount
  },
)

export const getHasAgenticWorkflows = createSelector(
  [getLicensedSkillsCount, getProprietarySkillsCount, getIsSolutionsCountCurrent, getIsSkillsDeliveryEnabled],
  (
    licensedSkillsCount: number | undefined,
    proprietarySkillsCount: number | undefined,
    isCurrent: boolean,
    skillsDeliveryEnabled: boolean,
  ): boolean => {
    if (!isCurrent) return false
    return skillsDeliveryEnabled && (!!licensedSkillsCount || !!proprietarySkillsCount)
  },
)
